<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useNotificationStore } from '@/stores/notification'

const router = useRouter()
const userStore = useUserStore()
const notificationStore = useNotificationStore()

const search = ref('')
const pages = ref(1)
const page = ref(1)
const perPage = ref(100)
const nameQuery = ref('') // valor efetivo enviado ao backend
const minSearch = 3
const dialog = ref(false)
const userIdToDelete = ref(null)

// simple debounce implementation
let debounceTimer = null
function debounceSetName(val, delay = 500) {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    nameQuery.value = val
    page.value = 1 // reset page when new search applied
  }, delay)
}

onMounted(() => {
  userStore.fetchUsuarios({ page: page.value, per_page: perPage.value, name: nameQuery.value })
  pages.value = userStore.dataUsuariosMeta?.last_page || 1
})

watch(search, (val) => {
  const trimmed = (val || '').trim()
  if (trimmed.length >= minSearch || trimmed.length === 0) {
    debounceSetName(trimmed)
  }
  // if less than minSearch and not empty, don't update nameQuery (no request)
})

watch([page, perPage, nameQuery], ([p, pp, name]) => {
  userStore.fetchUsuarios({ page: p, per_page: pp, name })
})

const edit = (id) => {
  router.push({ name: 'UsuarioForm', params: { id } })
}

const create = () => {
  router.push({ name: 'UsuarioForm' })
}

const confirmDelete = (id) => {
  userIdToDelete.value = id
  dialog.value = true
}

const deleteUser = async () => {
  if (userIdToDelete.value) {
    await userStore.deleteUsuario(userIdToDelete.value)
    // refetch usando paginação/pesquisa atuais
    await userStore.fetchUsuarios({ page: page.value, per_page: perPage.value, name: nameQuery.value })
    dialog.value = false
    notificationStore.showNotification('Usuário deletado com sucesso!', 'success')
  }
}

const usuarios = computed(() => {
  // support for API returning { data: [...], total, page, per_page } or plain array
  if (!userStore.data) return []
  return Array.isArray(userStore.data.data) ? userStore.data.data : userStore.data
})
const loading = computed(() => userStore.loading)
const error = computed(() => userStore.error)

</script>

<template>
  <div>
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <h1 class="text-h4">Gerenciar Usuários</h1>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="create">
          <v-icon start>mdi-plus</v-icon>
          Novo Usuário
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" closable class="mb-4">
      {{ error }}
    </v-alert>

    <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

    <v-data-table :headers="[
      { title: 'Situação', value: 'situacao', sortable: false },
      { title: 'Nome', value: 'nome' },
      { title: 'Email', value: 'email' },
      { title: 'Perfil', value: 'perfil.descricao' },
      { title: 'Ações', value: 'actions', sortable: false }
    ]" :items="usuarios" :search="search" class="custom-table" :page="page" :items-per-page="perPage" :pages="pages">
      <template #top>
        <v-text-field v-model="search" label="Pesquisar" prepend-inner-icon="mdi-magnify" variant="outlined"
          single-line></v-text-field>
      </template>

      <template #item="{ item }">
        <tr>
          <td>
            <v-chip size="x-small" :color="item.situacao == 1 ? 'success' : 'error'" class="ml-1">{{ item.situacao == 1
              ? "Ativo" : "Inativo" }}</v-chip>
          </td>
          <td>{{ item.nome }}</td>
          <td>{{ item.email }}</td>
          <td>{{ item.perfil?.descricao }}</td>
          <td>
            <v-icon class="mr-3" color="medium-emphasis" icon="mdi-pencil" size="large" @click="edit(item.id)"></v-icon>
            <v-icon class="ml-2" color="medium-emphasis" icon="mdi-delete" size="large"
              @click="confirmDelete(item.id)"></v-icon>
          </td>
        </tr>
      </template>

      <template #bottom>
        <div class="d-flex justify-space-between align-center px-4 py-2">
          <div class="d-flex align-center">
            <span class="text-caption text-secondary mr-4">
              Total de {{ userStore.dataUsuariosMeta?.total || 0 }} usuário{{ userStore.dataUsuariosMeta?.total !== 1 ?
                's' : '' }}
            </span>

            <v-select v-model="perPage" :items="[10, 25, 50, 100]" label="Itens por página" variant="outlined" dense
              hide-details style="width: 140px" @update:model-value="() => page = 1"></v-select>
          </div>

          <v-pagination v-if="userStore.dataUsuariosMeta?.last_page > 1" :model-value="page"
            :length="userStore.dataUsuariosMeta?.last_page" :total-visible="5"
            @update:model-value="(newPage) => page = newPage" rounded class="my-2"></v-pagination>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
        <v-card-text>Tem certeza de que deseja excluir este usuário?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="deleteUser">Excluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped></style>
